/**
 * /me/pending-approvals — central de pendencias do usuario.
 *
 * 3 tabs:
 *  1. **HITL**: aprovacoes de tools (legado M5).
 *  2. **Instalacoes**: install requests de agentes onde o user
 *     pode decidir (manager owner_team OR admin OR approval_responsible).
 *  3. **Publicacoes**: publish requests onde o user pode decidir.
 *
 * Substitui o antigo HitlInboxPage (single-tab) — FE8-4 (M6).
 */

import { CheckCircle2, ShieldAlert, XCircle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type {
  AgentInstallRequest,
  AgentPublishRequest,
  HitlRequest,
} from "@/api/types";
import { tokenStorage } from "@/auth/tokenStorage";
import { EmptyState } from "@/components/feedback/EmptyState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { ApprovalAgentSummary } from "@/features/agents/components/ApprovalAgentSummary";
import {
  useDecideInstall,
  usePendingInstallRequests,
  usePendingPublishRequests,
} from "@/features/agents/hooks/useAgentInstall";
import { useDecidePublish } from "@/features/agents/hooks/useAgentPublish";
import { useHitlRequests } from "@/features/hitl/hooks/useHitl";
import { HitlBanner } from "@/features/threads/components/HitlBanner";
import { getAgentIcon } from "@/lib/agent-icons";
import { formatDateTime } from "@/lib/date";
import { openSseStream } from "@/lib/sseClient";

// --- HITL tab --------------------------------------------------------------

function HitlTab() {
  const { t } = useTranslation("hitl");
  const { data, isLoading, isError, error, refetch } = useHitlRequests();
  const [decideRow, setDecideRow] = useState<HitlRequest | null>(null);

  async function decideAndDrain(
    hitlId: string,
    payload: {
      decision: "approve" | "reject";
      comment?: string;
      tool_input_override?: Record<string, unknown>;
    },
  ) {
    const token = await tokenStorage.getToken();
    await new Promise<void>((resolve) => {
      openSseStream(
        {
          path: `/hitl-requests/${hitlId}/decide`,
          method: "POST",
          body: payload,
        },
        {
          token,
          onEvent: () => {},
          onClose: () => resolve(),
          onError: () => resolve(),
        },
      );
    });
    setDecideRow(null);
    void refetch();
  }

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <div className="space-y-3">
      {data && data.length === 0 ? (
        <EmptyState title={t("title")} description={t("empty")} />
      ) : (
        <ul className="space-y-2">
          {data?.map((row) => (
            <li
              key={row.id}
              role="button"
              tabIndex={0}
              onClick={() => setDecideRow(row)}
              className="shadow-card flex cursor-pointer items-start gap-3 rounded-2xl border border-line bg-surface p-4 transition hover:border-brand-300 hover:shadow-hover"
            >
              <ShieldAlert className="mt-0.5 h-4 w-4 text-yellow-600" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <code className="font-mono text-sm">{row.tool_name}</code>
                  <Badge variant="outline" className="text-xs">
                    {t(`status.${row.status}`)}
                  </Badge>
                  {row.task_id ? (
                    <Link
                      to={`/tasks/${row.task_id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs text-brand-600 underline"
                    >
                      {t("openTask")}
                    </Link>
                  ) : null}
                </div>
                <p className="text-xs text-ink-500">
                  {new Date(row.created_at).toLocaleString("pt-BR")}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!decideRow} onOpenChange={(o) => !o && setDecideRow(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{t("banner.title")}</DialogTitle>
          </DialogHeader>
          {decideRow ? (
            <HitlBanner
              hitlRequestId={decideRow.id}
              toolName={decideRow.tool_name}
              toolInput={decideRow.tool_input}
              onDecide={(payload) => decideAndDrain(decideRow.id, payload)}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- shared decide drawer --------------------------------------------------

interface DecideDrawerProps {
  open: boolean;
  title: string;
  /** Quando presente, mostra um resumo do agente acima das observações. */
  agentId?: string;
  ownerTeamName?: string | null;
  onClose: () => void;
  onDecide: (decision: "approve" | "reject", comment: string) => Promise<void>;
  isPending: boolean;
}

function DecideDrawer({
  open,
  title,
  agentId,
  ownerTeamName,
  onClose,
  onDecide,
  isPending,
}: DecideDrawerProps) {
  const { t } = useTranslation("common");
  const [comment, setComment] = useState("");

  async function handle(decision: "approve" | "reject") {
    await onDecide(decision, comment);
    setComment("");
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {agentId ? (
            <ApprovalAgentSummary
              agentId={agentId}
              ownerTeamName={ownerTeamName}
            />
          ) : null}
          <Textarea
            placeholder={t("pages.approvals.commentPlaceholder")}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => handle("reject")}
              disabled={isPending}
            >
              <XCircle className="h-4 w-4" />
              {t("pages.approvals.reject")}
            </Button>
            <Button onClick={() => handle("approve")} disabled={isPending}>
              <CheckCircle2 className="h-4 w-4" />
              {t("pages.approvals.approve")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- Install tab -----------------------------------------------------------

function InstallTab() {
  const { t } = useTranslation("common");
  const { data, isLoading, isError, error, refetch } =
    usePendingInstallRequests();
  const [decideRow, setDecideRow] = useState<AgentInstallRequest | null>(null);
  const decide = useDecideInstall(decideRow?.agent_id ?? "");

  async function handleDecide(decision: "approve" | "reject", comment: string) {
    if (!decideRow) return;
    try {
      await decide.mutateAsync({
        requestId: decideRow.id,
        body: { decision, comment: comment || undefined },
      });
      toast.success(
        decision === "approve"
          ? t("pages.approvals.toasts.approved")
          : t("pages.approvals.toasts.rejected"),
      );
      setDecideRow(null);
      void refetch();
    } catch (err) {
      toastApiError(err);
    }
  }

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <div className="space-y-3">
      {data && data.length === 0 ? (
        <EmptyState
          title={t("pages.approvals.noInstalls")}
          description={t("pages.approvals.noInstallsHint")}
        />
      ) : (
        <ul className="space-y-2">
          {data?.map((row) => {
            const AgentIcon = getAgentIcon(row.agent_icon ?? null);
            return (
              <li
                key={row.id}
                role="button"
                tabIndex={0}
                onClick={() => setDecideRow(row)}
                className="shadow-card flex cursor-pointer items-start gap-3 rounded-2xl border border-line bg-surface p-4 transition hover:border-brand-300 hover:shadow-hover"
              >
                <AgentIcon className="mt-0.5 h-4 w-4 text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-ink-950">
                    {row.agent_name ?? row.agent_id.slice(0, 8)}
                  </p>
                  <p className="text-xs text-ink-500">
                    {t("pages.approvals.requestedBy")}{" "}
                    {row.requested_by_name ?? row.requested_by_user_id.slice(0, 8)}{" "}
                    · {formatDateTime(row.created_at)}
                  </p>
                </div>
                <Badge variant="outline" className="text-xs">
                  {row.status}
                </Badge>
              </li>
            );
          })}
        </ul>
      )}

      <DecideDrawer
        open={!!decideRow}
        title={t("pages.approvals.approveInstall")}
        agentId={decideRow?.agent_id}
        ownerTeamName={decideRow?.agent_owner_team_name}
        onClose={() => setDecideRow(null)}
        onDecide={handleDecide}
        isPending={decide.isPending}
      />
    </div>
  );
}

// --- Publish tab -----------------------------------------------------------

function PublishTab() {
  const { t } = useTranslation("common");
  const { data, isLoading, isError, error, refetch } =
    usePendingPublishRequests();
  const [decideRow, setDecideRow] = useState<AgentPublishRequest | null>(null);
  const decide = useDecidePublish(decideRow?.agent_id ?? "");

  async function handleDecide(decision: "approve" | "reject", comment: string) {
    if (!decideRow) return;
    try {
      await decide.mutateAsync({
        requestId: decideRow.id,
        body: { decision, comment: comment || undefined },
      });
      toast.success(
        decision === "approve"
          ? t("pages.approvals.toasts.published")
          : t("pages.approvals.toasts.rejected"),
      );
      setDecideRow(null);
      void refetch();
    } catch (err) {
      toastApiError(err);
    }
  }

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <div className="space-y-3">
      {data && data.length === 0 ? (
        <EmptyState
          title={t("pages.approvals.noPublishes")}
          description={t("pages.approvals.noPublishesHint")}
        />
      ) : (
        <ul className="space-y-2">
          {data?.map((row) => {
            const AgentIcon = getAgentIcon(row.agent_icon ?? null);
            return (
              <li
                key={row.id}
                role="button"
                tabIndex={0}
                onClick={() => setDecideRow(row)}
                className="shadow-card flex cursor-pointer items-start gap-3 rounded-2xl border border-line bg-surface p-4 transition hover:border-brand-300 hover:shadow-hover"
              >
                <AgentIcon className="mt-0.5 h-4 w-4 text-purple-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-ink-950">
                    {row.agent_name ?? row.agent_id.slice(0, 8)}
                  </p>
                  <p className="text-xs text-ink-500">
                    {t("pages.approvals.requestedBy")}{" "}
                    {row.requested_by_name ?? row.requested_by_user_id.slice(0, 8)}{" "}
                    · {formatDateTime(row.created_at)}
                  </p>
                </div>
                <Badge variant="outline" className="text-xs">
                  {row.status}
                </Badge>
              </li>
            );
          })}
        </ul>
      )}

      <DecideDrawer
        open={!!decideRow}
        title={t("pages.approvals.approvePublish")}
        agentId={decideRow?.agent_id}
        ownerTeamName={decideRow?.agent_owner_team_name}
        onClose={() => setDecideRow(null)}
        onDecide={handleDecide}
        isPending={decide.isPending}
      />
    </div>
  );
}

// --- Page ------------------------------------------------------------------

export function HitlInboxPage() {
  const { t } = useTranslation("common");
  const hitl = useHitlRequests();
  const publish = usePendingPublishRequests();
  const install = usePendingInstallRequests();

  const hitlCount = hitl.data?.length ?? 0;
  const publishCount = publish.data?.length ?? 0;
  const installCount = install.data?.length ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("pages.approvals.title")}
        description={t("pages.approvals.description")}
      />

      <Tabs defaultValue="hitl">
        <TabsList>
          <TabsTrigger value="hitl">
            {t("pages.approvals.tabs.hitl")}
            {hitlCount > 0 ? (
              <Badge variant="destructive" className="ml-2 text-xs">
                {hitlCount}
              </Badge>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="install">
            {t("pages.approvals.tabs.install")}
            {installCount > 0 ? (
              <Badge variant="destructive" className="ml-2 text-xs">
                {installCount}
              </Badge>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="publish">
            {t("pages.approvals.tabs.publish")}
            {publishCount > 0 ? (
              <Badge variant="destructive" className="ml-2 text-xs">
                {publishCount}
              </Badge>
            ) : null}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="hitl" className="pt-3">
          <HitlTab />
        </TabsContent>
        <TabsContent value="install" className="pt-3">
          <InstallTab />
        </TabsContent>
        <TabsContent value="publish" className="pt-3">
          <PublishTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
